import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Box, Collapse, Stack } from '@mui/material';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import {
  AgreementPricesBarChart,
  computePercentChange,
  DashboardGroupHeading,
  DashboardPeriod,
  getPeriodRange,
  KeyFigureTile,
  PeriodSelect,
} from '@retail/dashboard/components';
import { DashboardMessageKey, useDashboardTFunction } from '@retail/dashboard/i18n';
import {
  useFetchCentralAssortmentsInfoItems,
  useFetchNewAssortmentItems,
  useFetchUpdatedAgreementPrices,
} from '@retail/dashboard/data-access';
import { useFetchAssortmentPrices } from '@retail/my-assortment/data-access';
import { useFetchTotalFileDownloadStatus } from '@retail/export/data-access';
import { useFetchMemberCampaigns } from '@retail/campaign/data-access';
import { formatPrice } from '@retail/products/utils';
import { mgPrisRoutes } from '@retail/mgpris/config';
import { KeyFiguresDetailPanel } from './KeyFiguresDetailPanel';
import { CampaignsPanel } from './CampaignsPanel';
import { CentralAssortmentsPanel } from './CentralAssortmentsPanel';

const DATE_FORMAT = 'YYYY-MM-DD';

// Summary window matches the export page: recalculations within the last month.
const TOTALFIL_FROM = dayjs().subtract(1, 'month').format('YYYY-MM-DDTHH:mm');

type ExpandedPanel = 'period' | 'future' | 'campaigns' | 'centralAssortments';

const DETAIL_PANEL_ID = 'key-figures-detail-panel';
const DETAIL_HEADING_ID = 'key-figures-detail-heading';

const panelTitleMap: Record<ExpandedPanel, DashboardMessageKey> = {
  period: 'dashboard.keyFigures.agreementPrices',
  future: 'dashboard.keyFigures.futureAgreementPrices',
  campaigns: 'dashboard.keyFigures.activeCampaigns',
  centralAssortments: 'dashboard.keyFigures.centralAssortmentUpdates',
};

const sumCounts = (items?: { count: number }[]) =>
  (items ?? []).reduce((total, item) => total + item.count, 0);

interface Props {
  period: DashboardPeriod;
  onPeriodChange: (period: DashboardPeriod) => void;
  periods: readonly DashboardPeriod[];
}

export const KeyFiguresBlock = ({ period, onPeriodChange, periods }: Props) => {
  const t = useDashboardTFunction();
  const { i18n } = useTranslation();
  const { assortmentId } = useSelectedOrgUnit();

  const [expanded, setExpanded] = useState<ExpandedPanel | null>(null);
  const toggle = (panel: ExpandedPanel) => setExpanded((prev) => (prev === panel ? null : panel));

  const range = useMemo(() => getPeriodRange(period), [period]);
  const futureRange = useMemo(() => getPeriodRange('future'), []);

  const assortment = useFetchAssortmentPrices({
    assortmentId,
    refetchOnWindowFocus: false,
    suspense: false,
  });

  const newItemsCurrent = useFetchNewAssortmentItems({
    assortmentId,
    from: range.from.format(DATE_FORMAT),
    to: range.to.format(DATE_FORMAT),
    suspense: false,
    refetchOnWindowFocus: false,
  });

  const agreementSpan = useFetchUpdatedAgreementPrices({
    assortmentId,
    from: range.previousFrom.format(DATE_FORMAT),
    to: range.to.format(DATE_FORMAT),
    suspense: false,
    refetchOnWindowFocus: false,
  });
  const agreementFuture = useFetchUpdatedAgreementPrices({
    assortmentId,
    from: futureRange.from.format(DATE_FORMAT),
    to: futureRange.to.format(DATE_FORMAT),
    suspense: false,
    refetchOnWindowFocus: false,
  });

  const centralCurrent = useFetchCentralAssortmentsInfoItems({
    assortmentId,
    from: range.from.format(DATE_FORMAT),
    to: range.to.format(DATE_FORMAT),
    suspense: false,
    refetchOnWindowFocus: false,
  });

  const campaigns = useFetchMemberCampaigns({
    assortmentId,
    startDate: dayjs().format(DATE_FORMAT),
    suspense: false,
  });

  const totalfil = useFetchTotalFileDownloadStatus({
    assortmentId,
    from: TOTALFIL_FROM,
    refetchOnWindowFocus: false,
    suspense: false,
  });

  const totalItems = assortment.data?.totalNumberOfItems ?? 0;
  const newItems = sumCounts(newItemsCurrent.data);

  const { agreementCurrentData, agreementPreviousData } = useMemo(() => {
    const boundary = range.from.format(DATE_FORMAT);
    const items = agreementSpan.data ?? [];
    return {
      agreementCurrentData: items.filter((item) => item.date >= boundary),
      agreementPreviousData: items.filter((item) => item.date < boundary),
    };
  }, [agreementSpan.data, range.from]);

  const currentSum = sumCounts(agreementCurrentData);
  const previousSum = sumCounts(agreementPreviousData);
  const futureSum = sumCounts(agreementFuture.data);

  const centralUpdatesCurrent =
    sumCounts(centralCurrent.data?.newItemCount) + sumCounts(centralCurrent.data?.updatedItemCount);
  const centralLoading = centralCurrent.isLoading;
  const centralError = centralCurrent.isError;

  const { activeCount, futureCount, sortedCampaigns } = useMemo(() => {
    const now = dayjs();
    const list = campaigns.data ?? [];
    return {
      activeCount: list.filter(
        (c) => !now.isBefore(dayjs(c.validFrom)) && !now.isAfter(dayjs(c.validTo))
      ).length,
      futureCount: list.filter((c) => dayjs(c.validFrom).isAfter(now)).length,
      sortedCampaigns: [...list].sort(
        (a, b) => dayjs(a.validFrom).valueOf() - dayjs(b.validFrom).valueOf()
      ),
    };
  }, [campaigns.data]);

  const totalfilLastCalculated = totalfil.data?.lastRecalculated;
  const totalfilHasBeenCalculated =
    Boolean(totalfilLastCalculated) && dayjs(totalfilLastCalculated).isValid();
  const totalfilSupplierCount = totalfil.data?.suppliers?.length ?? 0;

  const assortmentLoading = assortment.isLoading || newItemsCurrent.isLoading;
  const assortmentError = assortment.isError || newItemsCurrent.isError;
  const agreementLoading = agreementSpan.isLoading || agreementFuture.isLoading;
  const agreementError = agreementSpan.isError || agreementFuture.isError;

  const errorMessage = t('dashboard.keyFigures.loadError');
  const retryLabel = t('dashboard.messages.retry');

  return (
    <Stack gap={1.5}>
      <DashboardGroupHeading
        title={t('dashboard.keyFigures.title')}
        icon={<InsightsOutlinedIcon fontSize="small" />}
        action={<PeriodSelect value={period} onChange={onPeriodChange} periods={periods} />}
      />

      <Box
        sx={{
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)',
          },
        }}
      >
        <KeyFigureTile
          label={t('dashboard.keyFigures.assortmentItems')}
          value={totalItems.toLocaleString(i18n.language)}
          caption={t('dashboard.keyFigures.newInPeriod', { count: newItems })}
          isLoading={assortmentLoading}
          isError={assortmentError}
          errorMessage={errorMessage}
          retryLabel={retryLabel}
          onRetry={() => {
            assortment.refetch();
            newItemsCurrent.refetch();
          }}
          to={mgPrisRoutes.assortment.getFullLinkPath()}
        />
        <KeyFigureTile
          label={t('dashboard.keyFigures.agreementPrices')}
          value={formatPrice(currentSum, i18n.language)}
          change={computePercentChange(currentSum, previousSum)}
          caption={t('dashboard.period.vsPrevious')}
          isLoading={agreementLoading}
          isError={agreementError}
          errorMessage={errorMessage}
          retryLabel={retryLabel}
          onRetry={() =>
            agreementSpan.refetch()
          }
          selected={expanded === 'period'}
          onClick={() => toggle('period')}
          controls={DETAIL_PANEL_ID}
        />
        <KeyFigureTile
          label={t('dashboard.keyFigures.futureAgreementPrices')}
          value={formatPrice(futureSum, i18n.language)}
          caption={t('dashboard.oppdaterteAvtalepriser.futureCaption')}
          isLoading={agreementLoading}
          isError={agreementError}
          errorMessage={errorMessage}
          retryLabel={retryLabel}
          onRetry={() => {
            agreementFuture.refetch();
          }}
          selected={expanded === 'future'}
          onClick={() => toggle('future')}
          controls={DETAIL_PANEL_ID}
        />
        <KeyFigureTile
          label={t('dashboard.keyFigures.totalfilLastCalculated')}
          value={
            totalfilHasBeenCalculated
              ? // @ts-ignore
                dayjs(totalfilLastCalculated).fromNow()
              : t('dashboard.totalfil.never')
          }
          caption={
            totalfilHasBeenCalculated
              ? t('dashboard.totalfil.calculatedDetail', {
                  count: totalfilSupplierCount,
                })
              : undefined
          }
          isLoading={totalfil.isLoading}
          isError={totalfil.isError}
          errorMessage={errorMessage}
          retryLabel={retryLabel}
          onRetry={() => totalfil.refetch()}
          to={mgPrisRoutes.export.getFullLinkPath()}
        />
        <KeyFigureTile
          label={t('dashboard.keyFigures.centralAssortmentUpdates')}
          value={centralUpdatesCurrent.toLocaleString(i18n.language)}
          caption={t('dashboard.keyFigures.centralUpdatesCaption')}
          isLoading={centralLoading}
          isError={centralError}
          errorMessage={errorMessage}
          retryLabel={retryLabel}
          onRetry={() => centralCurrent.refetch()}
          selected={expanded === 'centralAssortments'}
          onClick={() => toggle('centralAssortments')}
          controls={DETAIL_PANEL_ID}
        />
        <KeyFigureTile
          label={t('dashboard.keyFigures.activeCampaigns')}
          value={activeCount.toLocaleString(i18n.language)}
          caption={t('dashboard.keyFigures.campaignsSummary', { count: futureCount })}
          isLoading={campaigns.isLoading}
          isError={campaigns.isError}
          errorMessage={errorMessage}
          retryLabel={retryLabel}
          onRetry={() => campaigns.refetch()}
          selected={expanded === 'campaigns'}
          onClick={() => toggle('campaigns')}
          controls={DETAIL_PANEL_ID}
        />
      </Box>

      <Collapse in={expanded !== null} unmountOnExit>
        <KeyFiguresDetailPanel
          id={DETAIL_PANEL_ID}
          headingId={DETAIL_HEADING_ID}
          title={expanded && t(panelTitleMap[expanded])}
          closeLabel={t('dashboard.keyFigures.closeDetails')}
          onClose={() => setExpanded(null)}
        >
          {expanded === 'campaigns' && <CampaignsPanel campaigns={sortedCampaigns} />}
          {expanded === 'centralAssortments' && <CentralAssortmentsPanel period={period} />}
          {(expanded === 'period' || expanded === 'future') && (
            <Box>
              <AgreementPricesBarChart
                data={(expanded === 'future' ? agreementFuture.data : agreementCurrentData) ?? []}
              />
            </Box>
          )}
        </KeyFiguresDetailPanel>
      </Collapse>
    </Stack>
  );
};
